/**
 * Utility functions for building offer request payloads
 * Extracted from DeviceSpecifications.tsx
 */

import type { CalculateOfferRequest } from '../../../lib/calculateOffer';
import type { DynamicQuestion, SellSpecs, DynamicSpecItem } from '../types';

/**
 * Build offer request payload for calculateOffer API
 * 
 * @param modelName - Device model key from Supabase
 * @param selectedStorage - Selected storage option
 * @param answers - Dynamic answers state (questionKey -> optionKey)
 * @param hasDynamicQuestions - Whether dynamic questions are loaded
 * @param isIPhone - Whether device is iPhone
 * @param legacyState - Legacy state variables (for backward compatibility)
 * @returns CalculateOfferRequest payload or null if required fields missing
 */
export function buildOfferRequestPayload(
  modelName: string,
  selectedStorage: string,
  answers: Record<string, string>,
  hasDynamicQuestions: boolean,
  isIPhone: boolean,
  legacyState?: {
    selectedCondition?: string;
    selectedNetworkType?: string;
    selectedBattery?: string;
    selectedFunctionality?: string;
    selectedScreenCondition?: string;
    selectedHousingCondition?: string;
    selectedBatteryCapacity?: string;
  }
): CalculateOfferRequest | null {
  // Only modelName is truly required - storage is OPTIONAL
  if (!modelName) {
    console.log('[buildOfferRequestPayload] Missing modelName, returning null');
    return null;
  }

  // modelName is the device_models.key from Supabase (e.g. "iphone-15-pro")
  const deviceModelKey = modelName;

  // Determine storage_key with fallback chain:
  // 1. Use selectedStorage if provided and not empty
  // 2. Check answers for storage-related keys (dynamic questions flow)
  // 3. Use 'default' as fallback - this is the PRIMARY behavior for devices without storage
  let storageKey = selectedStorage?.trim() || '';

  // Try to find storage in answers if not provided directly
  if (!storageKey) {
    const storageAnswerKeys = ['storage', 'storage_capacity', 'opslag', 'opslagcapaciteit'];
    for (const key of storageAnswerKeys) {
      if (answers[key]) {
        storageKey = answers[key];
        break;
      }
    }
  }

  // ALWAYS use 'default' if no storage - API handles this for base price calculation
  if (!storageKey) {
    storageKey = 'default';
  }

  // Build answers object: question_key -> option_key mapping
  // Use dynamic answers state (from Supabase questions) as primary source
  // Fallback to legacy state variables for backward compatibility during transition
  const answersPayload: Record<string, string> = { ...answers };

  // Legacy flow: merge legacy state variables if no dynamic questions
  if (!hasDynamicQuestions && legacyState) {
    // Legacy state variables (kept for backward compatibility)
    if (legacyState.selectedCondition && !answersPayload.condition) {
      answersPayload.condition = legacyState.selectedCondition;
    }
    if (legacyState.selectedNetworkType && !answersPayload.networkType) {
      answersPayload.networkType = legacyState.selectedNetworkType;
    }
    if (legacyState.selectedBattery && !answersPayload.battery) {
      answersPayload.battery = legacyState.selectedBattery;
    }
    if (isIPhone) {
      if (legacyState.selectedFunctionality && !answersPayload.functionality) {
        answersPayload.functionality = legacyState.selectedFunctionality;
      }
      if (legacyState.selectedScreenCondition && !answersPayload.screenCondition) {
        answersPayload.screenCondition = legacyState.selectedScreenCondition;
      }
      if (legacyState.selectedHousingCondition && !answersPayload.housingCondition) {
        answersPayload.housingCondition = legacyState.selectedHousingCondition;
      }
      if (legacyState.selectedBatteryCapacity && !answersPayload.batteryCapacity) {
        answersPayload.batteryCapacity = legacyState.selectedBatteryCapacity;
      }
    }
  }

  console.log('[BUILD OFFER PAYLOAD]', { device_model_key: deviceModelKey, storage_key: storageKey, answers: answersPayload });

  return {
    device_model_key: deviceModelKey,
    storage_key: storageKey,
    answers: answersPayload,
  };
}

/**
 * Build specs payload for onSellClick (navigation/cart)
 *
 * @param selectedStorage - Selected storage option
 * @param answers - Dynamic answers state
 * @param hasDynamicQuestions - Whether dynamic questions are loaded
 * @param isIPhone - Whether device is iPhone
 * @param price - Calculated price (number | string | null)
 * @param modelName - Device model name
 * @param getDeviceImagePath - Function to get device image path
 * @param batteryHealthPercent - Battery health percent (iPhone specific)
 * @param legacyState - Legacy state variables (for backward compatibility)
 * @param questions - Dynamic questions array (for building dynamicSpecs with labels)
 * @param isOnRequest - Whether this is an On Request item (allows null price)
 * @returns SellSpecs payload or null if validation fails
 */
export function buildSellSpecs(
  selectedStorage: string,
  answers: Record<string, string>,
  hasDynamicQuestions: boolean,
  isIPhone: boolean,
  price: number | string | null,
  modelName: string,
  getDeviceImagePath: (modelName: string) => string,
  batteryHealthPercent?: string,
  legacyState?: {
    selectedNetworkType?: string;
    selectedBattery?: string;
    selectedCondition?: string;
    selectedFunctionality?: string;
    selectedScreenCondition?: string;
    selectedHousingCondition?: string;
    selectedBatteryCapacity?: string;
  },
  questions?: DynamicQuestion[],
  isOnRequest?: boolean,
  displayName?: string
): SellSpecs | null {
  // Determine storage: use selectedStorage, check answers, or use 'default'
  let storageValue = selectedStorage;
  if (!storageValue) {
    // Check answers for storage-related question keys
    const storageAnswerKeys = ['storage', 'storage_capacity', 'opslag', 'opslagcapaciteit'];
    for (const key of storageAnswerKeys) {
      if (answers[key]) {
        storageValue = answers[key];
        break;
      }
    }
  }

  // Use 'default' for devices without storage options
  if (!storageValue) {
    storageValue = 'default';
  }

  const specs: any = { storage: storageValue };

  // Use dynamic answers state if available, otherwise fallback to legacy state
  if (hasDynamicQuestions) {
    // Map dynamic answers to flat specs format (for backward compatibility)
    if (answers.networkType) specs.networkType = answers.networkType;
    if (answers.battery) specs.battery = answers.battery;
    if (answers.condition) specs.condition = answers.condition;
    if (answers.functionality) specs.functionality = answers.functionality;
    if (answers.screenCondition) specs.screenCondition = answers.screenCondition;
    if (answers.housingCondition) specs.housingCondition = answers.housingCondition;
    if (answers.batteryCapacity) specs.batteryCapacity = answers.batteryCapacity;

    // Handle battery health percent for iPhone (special case)
    if (isIPhone && batteryHealthPercent) {
      specs.batteryCapacity = `${batteryHealthPercent}%`;
    } else if (isIPhone && answers.batteryCapacity) {
      specs.batteryCapacity = answers.batteryCapacity;
    }

    // Build dynamicSpecs array with ALL answered questions and their labels
    // Stores both Dutch (for backend/calendar) and current language (for frontend display)
    // This is the primary source for displaying specs in cart/checkout
    if (questions && questions.length > 0) {
      const dynamicSpecs: DynamicSpecItem[] = [];

      for (const question of questions) {
        const answerKey = answers[question.questionKey];
        if (answerKey) {
          // Find the selected option to get its labels
          const selectedOption = question.options.find(opt => opt.optionKey === answerKey);
          if (selectedOption) {
            // Alleen voor de versie-vraag (opslag+connectiviteit / maat+GPS)
            // combineren we label + description tot "128GB · Wi-Fi".
            // Andere vragen tonen alleen het label — description is daar context
            // bij het kiezen, niet relevant voor cart/email.
            const qKey = question.questionKey.toLowerCase();
            const qLbl = (question.label || '').toLowerCase();
            const isVersie = qKey === 'versie' || qLbl.includes('versie') || qLbl.includes('version');

            const combine = (labelObj?: Record<string, string> | null, descObj?: Record<string, string> | null): Record<string, string> | undefined => {
              if (!labelObj) return undefined;
              if (!descObj) return labelObj;
              const out: Record<string, string> = {};
              for (const k of Object.keys(labelObj)) {
                out[k] = descObj[k] ? `${labelObj[k]} · ${descObj[k]}` : labelObj[k];
              }
              return out;
            };

            const optionLabelCombined = (isVersie && selectedOption.description)
              ? `${selectedOption.label} · ${selectedOption.description}`
              : selectedOption.label;
            const optionLabelsCombined = isVersie
              ? combine(selectedOption.labelRaw, selectedOption.descriptionRaw)
              : selectedOption.labelRaw;

            // Extract Dutch labels (for backend logs and calendar - always in Dutch)
            const questionNl = question.labelRaw?.nl || question.label || question.questionKey;
            const optionNl = optionLabelsCombined?.nl
              || (selectedOption.labelRaw?.nl ?? selectedOption.label)
              || selectedOption.optionKey;

            dynamicSpecs.push({
              questionKey: question.questionKey,
              optionKey: selectedOption.optionKey,
              // Dutch labels for backend/calendar
              question_nl: questionNl,
              option_nl: optionNl,
              // Current language labels for frontend display
              question_label: question.label,
              option_label: optionLabelCombined,
              // Full multilingual JSONB for dynamic re-resolution on language change
              question_labels: question.labelRaw || undefined,
              option_labels: optionLabelsCombined || selectedOption.labelRaw || undefined,
            });
          }
        }
      }

      if (dynamicSpecs.length > 0) {
        specs.dynamicSpecs = dynamicSpecs;
      }
    }
  } else if (legacyState) {
    // Legacy flow: use legacy state variables
    if (isIPhone) {
      if (legacyState.selectedFunctionality) specs.functionality = legacyState.selectedFunctionality;
      if (legacyState.selectedScreenCondition) specs.screenCondition = legacyState.selectedScreenCondition;
      if (legacyState.selectedHousingCondition) specs.housingCondition = legacyState.selectedHousingCondition;
      // For cart summary: if exact health provided, replace capacity label with "NN%"
      if (batteryHealthPercent) {
        specs.batteryCapacity = `${batteryHealthPercent}%`;
      } else if (legacyState.selectedBatteryCapacity) {
        specs.batteryCapacity = legacyState.selectedBatteryCapacity;
      }
    } else {
      if (legacyState.selectedNetworkType) specs.networkType = legacyState.selectedNetworkType;
      if (legacyState.selectedBattery) specs.battery = legacyState.selectedBattery;
      if (legacyState.selectedCondition) specs.condition = legacyState.selectedCondition;
    }
  }

  // Price validation:
  // - For On Request items: allow null/undefined price, set to 0 or special marker
  // - For normal items: must be a valid non-negative number
  if (isOnRequest) {
    // On Request items can proceed without a fixed price
    specs.price = typeof price === 'number' && price >= 0 ? price : 0;
    specs.isOnRequest = true;
    console.log('[buildSellSpecs] On Request item - allowing to proceed', { price: specs.price, isOnRequest: true });
  } else {
    // Normal items require valid price
    if (typeof price !== 'number' || price < 0) {
      console.warn('[buildSellSpecs] Invalid price detected, returning null to prevent cart addition', { price, isOnRequest });
      return null;
    }
    specs.price = price;
  }

  specs.imageSrc = getDeviceImagePath(modelName);
  if (displayName) specs.displayName = displayName;

  return specs;
}

